import { Center, Text3D } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useAtomValue } from 'jotai'
import { trackEvent } from '../api'
import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { useParams } from 'wouter'
import { clientAtom, languageAtom, minigameStateAtom, teamsAtom } from '../GlobalState'
import Moon from '../meshes/Moon'
import AlienHead from '../meshes/AlienHead'
import EarthModified from '../meshes/EarthModified'
import Mars from '../meshes/Mars'
import Neptune from '../meshes/Neptune'
import Rocket from '../meshes/Rocket'
import Saturn from '../meshes/Saturn'
import Star from '../meshes/Stars/Star'
import UfoNew from '../meshes/UfoNew'
import YutBonus from '../components/YutBonus'
import useStarRoll from '../shader/starRoll/StarRoll'
import { getSocket } from '../socket'
import { getNameFont } from '../logicHelpers/helpers'
import translations from '../dictionaries/translations'

const socket = getSocket()
const font = '/fonts/Luckiest Guy_Regular.json'
const GRID_WIDTH = 6
const GRID_HEIGHT = 8
const DISPLAY_SCALE = 1.5
const CELL_SIZE = 1.12 * DISPLAY_SCALE
const BUBBLE_Y = 0.18 * DISPLAY_SCALE

function Label({ children, color = 'white', position, size = 0.35 }) {
  return <Text3D font={getNameFont(String(children), font)} size={size * DISPLAY_SCALE} height={0.015} position={position} rotation={[-Math.PI / 2, 0, 0]}>
    <meshStandardMaterial color={color} />
    {children}
  </Text3D>
}

function Button({ children, position, onClick }) {
  const [hovered, setHovered] = useState(false)
  return <group position={position}>
    <mesh><boxGeometry args={[3.7 * DISPLAY_SCALE, 0.05 * DISPLAY_SCALE, DISPLAY_SCALE]} /><meshStandardMaterial color={hovered ? 'limegreen' : 'yellow'} /></mesh>
    <mesh position={[0, 0.04 * DISPLAY_SCALE, 0]} onPointerEnter={() => { setHovered(true); document.body.style.cursor = 'pointer' }} onPointerLeave={() => { setHovered(false); document.body.style.cursor = 'default' }} onPointerDown={(event) => { event.stopPropagation(); onClick() }}>
      <boxGeometry args={[3.7 * DISPLAY_SCALE, 0.1 * DISPLAY_SCALE, DISPLAY_SCALE]} /><meshStandardMaterial transparent opacity={0} />
    </mesh>
    <Center position={[0, 0.07 * DISPLAY_SCALE, 0]}><Text3D font={getNameFont(String(children), font)} size={0.38 * DISPLAY_SCALE} height={0.015} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color='black' />{children}</Text3D></Center>
  </group>
}

function BubbleShape({ type, specialKind = null }) {
  if (specialKind === 'redStar') return <Star position={[0, -0.22 * DISPLAY_SCALE, 0]} scale={0.5 * DISPLAY_SCALE} color='#ef3333' emissive='#ef3333' />
  if (specialKind === 'blueStar') return <Star position={[0, -0.22 * DISPLAY_SCALE, 0]} scale={0.5 * DISPLAY_SCALE} color='#38bdf8' emissive='#38bdf8' />
  if (specialKind === 'brownStar') return <Star position={[0, -0.22 * DISPLAY_SCALE, 0]} scale={0.5 * DISPLAY_SCALE} color='#a16207' emissive='#a16207' />
  if (specialKind === 'mars') return <Mars position={[0, -0.2 * DISPLAY_SCALE, 0]} scale={0.34 * DISPLAY_SCALE} />
  if (specialKind === 'neptune') return <Neptune position={[0, -0.2 * DISPLAY_SCALE, 0]} scale={0.42 * DISPLAY_SCALE} />
  if (specialKind === 'saturn') return <Saturn position={[0, -0.22 * DISPLAY_SCALE, 0]} scale={0.34 * DISPLAY_SCALE} />
  if (type === 'crescent') return <Moon position={[0, -0.22 * DISPLAY_SCALE, 0]} scale={0.22 * DISPLAY_SCALE} />
  if (type === 'alien') return <AlienHead position={[0, -0.28 * DISPLAY_SCALE, 1]} rotation={[-Math.PI / 2, 0, 0]} scale={0.7 * DISPLAY_SCALE} />
  if (type === 'rocket') return <Rocket position={[0, -0.12 * DISPLAY_SCALE, -0.1 * DISPLAY_SCALE]} rotation={[0, -Math.PI / 32, 0]} scale={DISPLAY_SCALE} />
  if (type === 'planet') return <EarthModified position={[0, -0.28 * DISPLAY_SCALE, 0]} scale={0.28 * DISPLAY_SCALE} rotate={true} />
  return <Star position={[0, -0.22 * DISPLAY_SCALE, 0]} scale={0.45 * DISPLAY_SCALE} color='yellow' />
}

function Bubble({ type, row, column, onDrop, active, isPopping, dropOrigin, rejectedSwap, successfulSwap, specialSpawn, specialKind = null }) {
  const [hovered, setHovered] = useState(false)
  const [dragPosition, setDragPosition] = useState(null)
  const dragging = useRef(false)
  const dragStart = useRef(null)
  const previousDropKey = useRef(null)
  const fallTransition = useRef(null)
  const rejectedSwapTransition = useRef(null)
  const successfulSwapTransition = useRef(null)
  const specialSpawnTransition = useRef(null)
  const previousSpecialSpawnId = useRef(null)
  const popProgress = useRef(null)
  const fallingBubble = useRef(null)
  const swapBubble = useRef(null)
  const x = (column - (GRID_WIDTH - 1) / 2) * CELL_SIZE
  const z = (row - (GRID_HEIGHT - 1) / 2) * CELL_SIZE

  useEffect(() => {
    if (!dropOrigin) {
      previousDropKey.current = null
      return
    }
    const dropKey = `${dropOrigin.id ?? ''}:${dropOrigin.row}:${dropOrigin.column}:${dropOrigin.fromRow}`
    if (previousDropKey.current === dropKey) return
    fallTransition.current = { elapsed: 0, distance: Math.max(0, row - dropOrigin.fromRow) }
    if (fallingBubble.current) {
      fallingBubble.current.scale.setScalar(1)
      fallingBubble.current.position.z = -CELL_SIZE * fallTransition.current.distance
    }
    if (swapBubble.current) {
      swapBubble.current.position.set(0, 0, 0)
      swapBubble.current.scale.setScalar(1)
    }
    previousDropKey.current = dropKey
  }, [dropOrigin, row])

  useEffect(() => {
    popProgress.current = isPopping ? 0 : null
    if (!isPopping && fallingBubble.current) fallingBubble.current.scale.setScalar(1)
  }, [isPopping])

  useEffect(() => {
    const isSwapBubble = rejectedSwap && (
      (rejectedSwap.fromRow === row && rejectedSwap.fromColumn === column) ||
      (rejectedSwap.toRow === row && rejectedSwap.toColumn === column)
    )
    if (!isSwapBubble) return
    const direction = rejectedSwap.fromRow === row && rejectedSwap.fromColumn === column ? 1 : -1
    rejectedSwapTransition.current = {
      elapsed: 0,
      x: direction * (rejectedSwap.toColumn - rejectedSwap.fromColumn) * CELL_SIZE,
      z: direction * (rejectedSwap.toRow - rejectedSwap.fromRow) * CELL_SIZE
    }
  }, [rejectedSwap, row, column])

  useEffect(() => {
    const isSwapBubble = successfulSwap && (
      (successfulSwap.fromRow === row && successfulSwap.fromColumn === column) ||
      (successfulSwap.toRow === row && successfulSwap.toColumn === column)
    )
    if (!isSwapBubble) return
    const direction = successfulSwap.fromRow === row && successfulSwap.fromColumn === column ? 1 : -1
    successfulSwapTransition.current = {
      elapsed: 0,
      x: direction * (successfulSwap.toColumn - successfulSwap.fromColumn) * CELL_SIZE,
      z: direction * (successfulSwap.toRow - successfulSwap.fromRow) * CELL_SIZE
    }
  }, [successfulSwap, row, column])

  useEffect(() => {
    if (!specialSpawn || previousSpecialSpawnId.current === specialSpawn.id) return
    const sourceCell = specialSpawn.cells?.find((cell) => cell.row === row && cell.column === column)
    if (!sourceCell) return
    previousSpecialSpawnId.current = specialSpawn.id
    specialSpawnTransition.current = {
      elapsed: 0,
      isTarget: specialSpawn.row === row && specialSpawn.column === column,
      x: (specialSpawn.column - column) * CELL_SIZE,
      z: (specialSpawn.row - row) * CELL_SIZE
    }
  }, [specialSpawn, row, column])

  useFrame((_, delta) => {
    if (fallTransition.current) {
      fallTransition.current.elapsed += delta
      const progress = Math.min(1, fallTransition.current.elapsed / 0.36)
      const easedProgress = 1 - Math.pow(1 - progress, 3)
      if (fallingBubble.current) fallingBubble.current.position.z = -CELL_SIZE * fallTransition.current.distance * (1 - easedProgress)
      if (progress === 1) fallTransition.current = null
    }
    if (popProgress.current !== null) {
      popProgress.current = Math.min(1, popProgress.current + delta / 0.3)
      if (fallingBubble.current) {
        const popScale = 1 - popProgress.current
        fallingBubble.current.scale.setScalar(popScale)
        fallingBubble.current.rotation.y = popProgress.current * Math.PI * 2
      }
    }
    if (rejectedSwapTransition.current && swapBubble.current) {
      rejectedSwapTransition.current.elapsed += delta
      const progress = Math.min(1, rejectedSwapTransition.current.elapsed / 0.32)
      const travel = progress < 0.5 ? progress * 2 : (1 - progress) * 2
      swapBubble.current.position.x = rejectedSwapTransition.current.x * travel
      swapBubble.current.position.z = rejectedSwapTransition.current.z * travel
      if (progress === 1) {
        swapBubble.current.position.set(0, 0, 0)
        rejectedSwapTransition.current = null
      }
    }
    if (successfulSwapTransition.current && swapBubble.current) {
      successfulSwapTransition.current.elapsed += delta
      const progress = Math.min(1, successfulSwapTransition.current.elapsed / 0.16)
      swapBubble.current.position.x = successfulSwapTransition.current.x * progress
      swapBubble.current.position.z = successfulSwapTransition.current.z * progress
      if (progress === 1) {
        swapBubble.current.position.set(0, 0, 0)
        successfulSwapTransition.current = null
      }
    }
    if (specialSpawnTransition.current && swapBubble.current) {
      specialSpawnTransition.current.elapsed += delta
      const progress = Math.min(1, specialSpawnTransition.current.elapsed / 0.3)
      const transition = specialSpawnTransition.current
      if (transition.isTarget) {
        swapBubble.current.scale.setScalar(0.35 + progress * 0.65)
      } else {
        swapBubble.current.position.x = transition.x * progress
        swapBubble.current.position.z = transition.z * progress
        swapBubble.current.scale.setScalar(1 - progress)
      }
      if (progress === 1) {
        swapBubble.current.position.set(0, 0, 0)
        if (transition.isTarget) swapBubble.current.scale.setScalar(1)
        specialSpawnTransition.current = null
      }
    }
  })

  function boardPoint(event) {
    const point = new THREE.Vector3()
    event.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), -BUBBLE_Y), point)
    return point
  }

  function startDrag(event) {
    if (!active) return
    event.stopPropagation()
    dragging.current = true
    dragStart.current = boardPoint(event)
    event.target.setPointerCapture?.(event.pointerId)
  }

  function moveDrag(event) {
    if (!dragging.current) return
    event.stopPropagation()
    const point = boardPoint(event)
    setDragPosition([point.x, 0.34 * DISPLAY_SCALE, point.z])
  }

  function endDrag(event) {
    if (!dragging.current) return
    event.stopPropagation()
    dragging.current = false
    event.target.releasePointerCapture?.(event.pointerId)
    setDragPosition(null)
    const point = boardPoint(event)
    const start = dragStart.current
    dragStart.current = null
    if (!start) return

    const deltaX = point.x - start.x
    const deltaZ = point.z - start.z
    if (Math.max(Math.abs(deltaX), Math.abs(deltaZ)) < CELL_SIZE * 0.15) return

    const horizontalSwipe = Math.abs(deltaX) > Math.abs(deltaZ)
    const swipeDirection = horizontalSwipe
      ? deltaX > 0 ? 'right' : 'left'
      : deltaZ > 0 ? 'down' : 'up'
    const targetColumn = column + (swipeDirection === 'right' ? 1 : swipeDirection === 'left' ? -1 : 0)
    const targetRow = row + (swipeDirection === 'down' ? 1 : swipeDirection === 'up' ? -1 : 0)
    if (targetRow >= 0 && targetRow < GRID_HEIGHT && targetColumn >= 0 && targetColumn < GRID_WIDTH) {
      onDrop(row, column, targetRow, targetColumn, swipeDirection)
    }
  }

  return <group position={dragPosition ?? [x, BUBBLE_Y, z]} onPointerEnter={() => { if (active) { setHovered(true); document.body.style.cursor = 'pointer' } }} onPointerLeave={() => { if (!dragging.current) { setHovered(false); document.body.style.cursor = 'default' } }} onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag}>
    <mesh position={[0, -0.02 * DISPLAY_SCALE, 0]}>
      <boxGeometry args={[CELL_SIZE, 0.06 * DISPLAY_SCALE, CELL_SIZE]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
    <group ref={fallingBubble}>
      <group ref={swapBubble}>
        <BubbleShape type={type} specialKind={specialKind} />
      </group>
    </group>
  </group>
}

function ResultTeamColumn({ team, teamName, players, playerScores, teamScore, winnerTeam, labels, language }) {
  const isRocketTeam = team === 0
  const color = isRocketTeam ? 'red' : 'turquoise'
  const isWinner = winnerTeam === team
  const columnX = isRocketTeam ? -3.25 : 3.25
  const totalScoreZ = 1.78 + players.length * 0.9
  const rewardZ = totalScoreZ + 1.5

  return <group position={[columnX, 0, -2.5]}>
    {isRocketTeam
      ? <Rocket position={[0, 0, -0.25]} rotation={[0, -Math.PI / 16, 0]} scale={1.55} />
      : <UfoNew position={[0, 0, 0]} scale={1.5} animate={false} />}
    {players.map((player, index) => <Center key={player.id} position={[0, 0.25, 1.25 + index * 0.9]}>
      <Text3D font={getNameFont(player.name, font)} size={0.34 * DISPLAY_SCALE} height={0.015} rotation={[-Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color={color} />
        {`${player.name} ${playerScores?.[player.id] ?? 0}`}
      </Text3D>
    </Center>)}
    <Center position={[0, 0.25, totalScoreZ]}>
      <Text3D font={getNameFont(labels.total[language], font)} size={0.3 * DISPLAY_SCALE} height={0.015} rotation={[-Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color={color} />
        {`${labels.total[language]} ${teamScore}`}
      </Text3D>
    </Center>
    {isWinner && <>
      <YutBonus position={[0, 0.18, rewardZ]} scale={0.75} alwaysShow enableClick={false} modelOnly />
      <Center position={[0.3, 0.25, rewardZ + 1.35]}><Text3D font={getNameFont(labels.freeYut[language], font)} size={0.24 * DISPLAY_SCALE} height={0.015} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color='yellow' />{labels.freeYut[language]}</Text3D></Center>
    </>}
  </group>
}

export default function BubbleMatchScene() {
  const client = useAtomValue(clientAtom)
  const language = useAtomValue(languageAtom)
  const teams = useAtomValue(teamsAtom)
  const state = useAtomValue(minigameStateAtom)
  const { id } = useParams()
  const roomId = id?.toUpperCase()
  const [continueSeconds, setContinueSeconds] = useState(10)
  const [RollStar] = useStarRoll()
  const handledBonusTrailIdsRef = useRef(new Set())
  const phase = state.phase ?? 'demo'
  const playerId = String(client?._id ?? client?.id ?? '')
  const board = state.boards?.[playerId] ?? []
  const specialBubbles = state.specialBubbles?.[playerId] ?? {}
  const bonusPopEvents = state.bonusPopEvents ?? []
  const poppingCells = new Set((state.poppingCells?.[playerId] ?? []).map(({ row, column }) => `${row}:${column}`))
  const dropOrigins = new Map((state.dropOrigins?.[playerId] ?? []).map((origin) => [`${origin.row}:${origin.column}`, origin]))
  const rejectedSwap = state.rejectedSwaps?.[playerId]
  const successfulSwap = state.successfulSwaps?.[playerId]
  const specialSpawn = state.specialSpawns?.[playerId]
  const boardLocked = Boolean(state.resolvingPlayerIds?.[playerId])
  const active = (phase === 'demo' || phase === 'playing') && !boardLocked
  const showBoard = phase === 'demo' || phase === 'playing' || (phase === 'countdown' && state.countdown === 1)
  const players = Object.values(state.players ?? {})
  const teamPlayers = [0, 1].map((team) => players.filter((player) => player.team === team))
  const teamScores = teamPlayers.map((playersForTeam) => playersForTeam.reduce(
    (score, player) => score + (state.playerScores?.[player.id] ?? 0),
    0
  ))
  const winnerTeam = Number.isInteger(state.winnerTeam) ? state.winnerTeam : null
  const text = translations.minigames.bubbleMatch
  const labels = translations.minigames.labels
  const resultMessage = winnerTeam === null ? state.message : `${teams[winnerTeam]?.name || (winnerTeam === 0 ? labels.rockets[language] : labels.ufos[language])} ${labels.wins[language]}`

  function emit(event, payload = {}) { if (roomId) socket.emit(event, { roomId, ...payload }) }
  useEffect(() => {
    for (const event of bonusPopEvents) {
      if (!event || event.playerId !== playerId || handledBonusTrailIdsRef.current.has(event.id)) continue
      handledBonusTrailIdsRef.current.add(event.id)
      const baseColor = event.kind === 'saturn'
        ? new THREE.Color('#f3aa35')
        : event.kind === 'neptune'
          ? new THREE.Color('#47ccff')
          : event.kind === 'redStar'
            ? new THREE.Color('#ef3333')
            : event.kind === 'blueStar'
              ? new THREE.Color('#38bdf8')
              : event.kind === 'brownStar'
                ? new THREE.Color('#a16207')
          : new THREE.Color('#fff29d')
      const cells = Array.isArray(event.cells) ? event.cells : []
      for (const cell of cells) {
        const row = Number(cell?.row)
        const column = Number(cell?.column)
        if (!Number.isInteger(row) || !Number.isInteger(column)) continue
        const x = (column - (GRID_WIDTH - 1) / 2) * CELL_SIZE
        const z = (row - (GRID_HEIGHT - 1) / 2) * CELL_SIZE
        for (let burst = 0; burst < 4; burst += 1) {
          RollStar({
            position: new THREE.Vector3(x, BUBBLE_Y + 0.12, z),
            rotation: new THREE.Vector3(0, (burst * Math.PI) / 2, 0),
            omitFactor: 8,
            size: 0.0007,
            scale: 0.2 + Math.random() * 0.08,
            color: baseColor,
            duration: 0.85,
            speedX: 2.2 + Math.random() * 1.2,
            speedY: 1.2 + Math.random() * 1.1,
            brightness: 0.3
          })
        }
      }
    }
  }, [bonusPopEvents, playerId, RollStar])

  useEffect(() => {
    if (phase !== 'finished') { setContinueSeconds(10); return undefined }
    if (continueSeconds === 0) { emit('minigameReturnToGame'); return undefined }
    const timeoutId = window.setTimeout(() => setContinueSeconds((seconds) => seconds - 1), 1000)
    return () => window.clearTimeout(timeoutId)
  }, [continueSeconds, phase])

  return <group name='bubble-match-minigame'>
    {phase !== 'finished' && <>
      {phase === 'demo' && <>
        <Center position={[0, 0.2, -9.75]}>
          <Label color='yellow' size={0.62} position={[0, 0, 0]}>{text.title[language]}</Label>
        </Center>
        <Center position={[0, 0.2, -7.95]}>
          <Label color='white' size={0.32} position={[0, 0, 0]}>{labels.howToPlay[language]}</Label>
        </Center>
      </>}
      {phase === 'playing' && <>
        <Label color='red' size={0.3} position={[-5.1, 0.2, -8.4]}>{`${labels.rockets[language]} ${teamScores[0]}`}</Label>
        <Label color='turquoise' size={0.3} position={[1.15, 0.2, -8.4]}>{`${labels.ufos[language]} ${teamScores[1]}`}</Label>
        {teamPlayers[0].map((player, index) => <Label key={player.id} color='red' size={0.23} position={[-5.1, 0.2, -7.75 + index * 0.48]}>{`${player.name} ${state.playerScores?.[player.id] ?? 0}`}</Label>)}
        {teamPlayers[1].map((player, index) => <Label key={player.id} color='turquoise' size={0.23} position={[1.15, 0.2, -7.75 + index * 0.48]}>{`${player.name} ${state.playerScores?.[player.id] ?? 0}`}</Label>)}
      </>}
      {showBoard && board.flatMap((row, rowIndex) => row.map((type, columnIndex) => <Bubble key={`${rowIndex}-${columnIndex}`} type={type} row={rowIndex} column={columnIndex} specialKind={specialBubbles[`${rowIndex}:${columnIndex}`] ?? null} active={active} isPopping={poppingCells.has(`${rowIndex}:${columnIndex}`)} dropOrigin={dropOrigins.get(`${rowIndex}:${columnIndex}`)} rejectedSwap={rejectedSwap} successfulSwap={successfulSwap} specialSpawn={specialSpawn} onDrop={(fromRow, fromColumn, toRow, toColumn, swipeDirection) => {
        if (fromRow !== toRow || fromColumn !== toColumn) emit('minigameSwapBubbles', { fromRow, fromColumn, toRow, toColumn, swipeDirection })
      }} />))}
      {phase === 'demo' && <>
        <Center position={[0, 0.2, 8.6]}><Label color='white' size={0.32} position={[0, 0, 0]}>{text.rule[language]}</Label></Center>
        <Button position={[-3, 0, 10.7]} onClick={() => { emit('minigameReturnToGame'); trackEvent({ surface: 'minigame', action: 'exit', category: 'ui', metadata: { type: 'bubble_match' } }) }}>{translations.minigames.actions.back[language]}</Button>
        <Button position={[3, 0, 10.7]} onClick={() => { emit('minigameStartGame'); trackEvent({ surface: 'minigame', action: 'minigame_started', category: 'gameplay', metadata: { type: 'bubble_match' } }) }}>{translations.minigames.actions.start[language]}</Button>
      </>}
      {phase === 'countdown' && <Label color='yellow' size={1.4} position={[-0.5, 0.25, 0]}>{`${state.countdown}...`}</Label>}
      {phase === 'playing' && <Label color='limegreen' size={0.42} position={[-0.45, 0.2, -8.4]}>{state.timeRemaining ?? 0}</Label>}
    </>}
    {phase === 'finished' && <>
      <Center position={[0, 0.25, -5.8]}><Text3D font={getNameFont(resultMessage || labels.timeUp[language], font)} size={0.78 * DISPLAY_SCALE} height={0.015} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color={winnerTeam === 0 ? 'red' : winnerTeam === 1 ? 'turquoise' : 'yellow'} />{resultMessage || labels.timeUp[language]}</Text3D></Center>
      <ResultTeamColumn team={0} teamName={teams[0]?.name || labels.rockets[language]} players={teamPlayers[0]} playerScores={state.playerScores} teamScore={state.scores?.[0] ?? 0} winnerTeam={winnerTeam} labels={labels} language={language} />
      <ResultTeamColumn team={1} teamName={teams[1]?.name || labels.ufos[language]} players={teamPlayers[1]} playerScores={state.playerScores} teamScore={state.scores?.[1] ?? 0} winnerTeam={winnerTeam} labels={labels} language={language} />
      <Button position={[0, 0, 5.8]} onClick={() => emit('minigameReturnToGame')}>{`${translations.minigames.actions.continue[language]} ${continueSeconds}`}</Button>
    </>}
  </group>
}
